import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShoppingBag, Search, Filter, Grid, List, SlidersHorizontal, TrendingUp, MapPin, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import ProductCard from '../components/shared/ProductCard';
import { productsAPI, publicAPI, growthAPI } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NextActionCards from '../components/guidance/NextActionCards';

export default function MarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [viewMode, setViewMode] = useState('grid');
  const [trendingProducts, setTrendingProducts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, catsRes] = await Promise.all([
          productsAPI.getAll(selectedCategory !== 'all' ? selectedCategory : undefined),
          publicAPI.getCategories(),
        ]);
        setProducts(productsRes.data);
        setCategories(catsRes.data.products || []);
        // Fetch trending (non-blocking)
        growthAPI.trendingProducts(6).then(r => setTrendingProducts(r.data)).catch(() => {});
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedCategory]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                <ShoppingBag className="text-white" size={28} />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl md:text-3xl text-zinc-900">Marketplace</h1>
                <p className="text-zinc-500">{filteredProducts.length} products available</p>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 bg-zinc-50 border-zinc-200 rounded-full h-11"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters Bar */}
      <section className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center gap-3 overflow-x-auto hide-scrollbar">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                selectedCategory === 'all'
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              data-testid="category-all"
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                  selectedCategory === cat
                    ? 'bg-zinc-900 text-white'
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
                data-testid={`category-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
              className="h-9 w-9 rounded-full"
              data-testid="grid-view-btn"
            >
              <Grid size={16} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
              className="h-9 w-9 rounded-full"
              data-testid="list-view-btn"
            >
              <List size={16} />
            </Button>
          </div>
        </div>
      </section>

      {/* Trending Products */}
      {trendingProducts.length > 0 && !searchQuery && selectedCategory === 'all' && (
        <section className="max-w-7xl mx-auto px-4 pt-6 pb-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center rounded-lg">
              <Flame size={16} className="text-white" />
            </div>
            <h2 className="font-bold text-lg text-zinc-900" data-testid="trending-section-title">Trending Now</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {trendingProducts.map((product, idx) => (
              <motion.div key={product.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.04 }}>
                <ProductCard product={product} compact />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Products Grid */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-4 lg:grid-cols-5' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
            {[...Array(10)].map((_, i) => (
              <div key={i} className="aspect-square bg-white rounded-2xl skeleton" />
            ))}
          </div>
        ) : filteredProducts.length > 0 ? (
          <motion.div 
            className={`grid ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-4 lg:grid-cols-5' : 'grid-cols-1 md:grid-cols-2'} gap-6`}
          >
            {filteredProducts.map((product, idx) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-zinc-200">
            <div className="w-20 h-20 mx-auto mb-4 bg-zinc-100 rounded-2xl flex items-center justify-center">
              <ShoppingBag className="text-zinc-400" size={40} />
            </div>
            <h3 className="font-display text-xl text-zinc-900 mb-2">No Products Found</h3>
            <p className="text-zinc-500 max-w-md mx-auto">
              {searchQuery ? `No products matching "${searchQuery}"` : 'No products in this category yet'}
            </p>
          </div>
        )}
      </section>

      {/* Cross-Mode Next Actions */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        <NextActionCards contextPage="marketplace" />
      </section>
    </div>
  );
}
